import React from 'react';

// Consistent pin mark used across nav, wizard headers, and empty states.
// Kept as a plain function component (no external icon library dependency)
// so its color is always explicitly controlled by the caller — this is
// what prevents contrast issues like the price-on-mint-background bug we
// hit earlier, since nothing here silently inherits a clashing default.
export function PinIcon({ size = 18, color = '#3B5B7D', style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 7.5 12.5.28.28.72.28 1 0C13 22 20 15.25 20 10c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
    </svg>
  );
}

// Brand mark v3 — the Fare Stamp (Aug 23, per Ali's report). Replaces
// the earlier clipped-corner-plus-dot design. Same ticket base, but the
// accent is now a rotated checkmark-in-a-ring — a "verified fare" stamp
// rather than a plain dot — chosen specifically because it satisfies a
// requirement the earlier marks didn't: an explicit, legible tie to
// "fare" itself, not just "travel" in general. Verified via real pixel
// tests, not assumption: the checkmark/ring survive cleanly from ~40px
// up, but blur into an indistinct smudge at 16-28px the same way the
// earlier torn-perforation detail did — so this is deliberately NOT
// used below 40px anywhere. LogoDot below is the tested fallback for
// that range (favicon.ico's small frames, any icon under ~40px).
export function LogoIcon({ size = 92, color = '#1E3A5F', accent = '#F4C11C', style = {} }) {
  return (
    <svg width={size} height={size * (46 / 92)} viewBox="24 54 92 46" style={style}>
      <path d="M28,58 L92,58 L110,72 L110,96 L28,96 Z" fill={color} />
      <circle cx="88" cy="77" r="16" fill="none" stroke={accent} strokeWidth="3" transform="rotate(-15 88 77)" />
      <path d="M80,77 L86,83 L97,70" fill="none" stroke={accent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" transform="rotate(-15 88 77)" />
    </svg>
  );
}

// Dot-only fallback (Aug 23, per Ali's report) — the pre-Fare-Stamp
// design, kept alive specifically for sizes where the stamp detail is
// proven to fail: favicon.ico's 16/24/32px frames and any app-icon
// render under ~40px. Verified legible all the way to 16px — this is
// the one that actually has to survive a real browser tab, so it stays
// deliberately minimal (outline + one dot, nothing else) rather than
// chasing the fuller mark's detail at a size that can't support it.
export function LogoDot({ size = 22, color = '#3B5B7D', accent, style = {} }) {
  const accentColor = accent || color;
  return (
    <svg width={size} height={size * (18 / 24)} viewBox="0 0 26 18" style={style}>
      <path
        d="M2,2 L18,2 L24,7 L24,16 L2,16 Z"
        fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round"
      />
      <circle cx="21.5" cy="5" r="2.3" fill={accentColor} />
    </svg>
  );
}

// Full lockup (Aug 23, per Ali's report) — Fare Stamp icon paired with
// the "FlyAtlast" wordmark, for hero-size use (the login page's brand
// panel is the one real caller). Replaces the earlier torn-perforation
// wordmark-split design entirely — same underlying accuracy lesson as
// LogoIcon above: fine detail (the old tear line, this mark's stamp)
// only belongs where it's proven to survive, and hero size (180px+)
// is comfortably above that. The icon portion reuses LogoIcon's exact
// tested path coordinates via a transform, rather than being redrawn,
// so its own proven legibility carries over unchanged. Two variants via
// `variant`: 'navy' for light backgrounds (default), 'white' for dark
// backgrounds (ticket and wordmark both flip; the yellow stamp accent
// never changes, in both variants) — never use 'white' on a light page,
// same caveat as before: the ticket has no border and disappears.
export function LogoLockup({ width = 240, variant = 'navy', style = {} }) {
  const height = width * (140 / 420);
  const ticketFill = variant === 'white' ? '#fff' : '#1E3A5F';
  const textFill = variant === 'white' ? '#1E3A5F' : '#fff';
  return (
    <svg width={width} height={height} viewBox="0 0 420 140" style={style}>
      <g transform="translate(-24.4,-56.4) scale(1.6)">
        <path d="M28,58 L92,58 L110,72 L110,96 L28,96 Z" fill={ticketFill} />
        <circle cx="88" cy="77" r="16" fill="none" stroke="#F4C11C" strokeWidth="3" transform="rotate(-15 88 77)" />
        <path d="M80,77 L86,83 L97,70" fill="none" stroke="#F4C11C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" transform="rotate(-15 88 77)" />
      </g>
      <text x="180" y="95" fontSize="46" fontWeight="800" fill={textFill} fontFamily="'Public Sans', sans-serif">FlyAtlast</text>
    </svg>
  );
}

// Compact fallback (Aug 22, per Ali's report) — for the 80-180px range
// where the full lockup's fine detail doesn't survive. This IS the
// nav-bar standard now (Aug 23, per Ali's report) — every page that
// used to pair LogoIcon with a separate "FlyAtlast" text now uses this
// exact treatment instead: plain wordmark, single yellow dot as the
// only accent — verified legible down to ~55px, well below where this
// is likely to ever actually render.
export function LogoCompact({ width = 140, color = '#1E3A5F', style = {} }) {
  const height = width * (60 / 210);
  return (
    <svg width={width} height={height} viewBox="0 0 210 60" style={style}>
      <text x="8" y="42" fontSize="34" fontWeight="800" fill={color} fontFamily="'Public Sans', sans-serif">FlyAtlast</text>
      <circle cx="196" cy="30" r="5" fill="#F4C11C" />
    </svg>
  );
}

// Price-tag icon for "View Deals" — replaces the 🏷️ emoji, which rendered
// with its own baked-in orange/brown tint on most systems and visually
// merged into the orange button background behind it. This SVG's color is
// always explicitly passed in, so it stays legible regardless of button color.
export function TagIcon({ size = 14, color = '#FDF6EC', style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ verticalAlign: '-2px', marginLeft: '4px', ...style }}>
      <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.41l9 9c.36.36.86.59 1.41.59s1.05-.23 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
    </svg>
  );
}

// Landing page feature icons — same plain-SVG, explicit-color approach as
// the icons above, so they never silently inherit a clashing default color.
export function PlaneIcon({ size = 24, color = '#FDF6EC', style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2.5 1.5V22l4-1 4 1v-1.5L13 19v-5.5z" />
    </svg>
  );
}

export function BedIcon({ size = 24, color = '#FDF6EC', style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M7 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm12-2h-8v4H3V4H1v15h2v-3h18v3h2v-8c0-2.21-1.79-4-4-4z" />
    </svg>
  );
}

export function BellIcon({ size = 24, color = '#FDF6EC', style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1z" />
    </svg>
  );
}

// PasswordVisibilityToggle — extracted (Sep 15, per Ali's report —
// real gap found live: Login.js had this show/hide eye toggle from
// the start, but ResetPasswordScreen.js and DeleteAccountScreen.js
// both grew their own password fields later without it, so typing a
// new password with no way to check it had no precedent to copy from
// at the time). Same two SVG paths Login.js already used, just
// pulled out here so all three screens render the exact same icon
// and behavior instead of three separate copies drifting apart later.
// Renders as the clickable eye/eye-off icon itself — caller owns the
// showPassword state and the actual input's type attribute.
export function PasswordVisibilityToggle({ visible, onClick, size = 19, color = '#5A6B78' }) {
  return (
    <span
      onClick={onClick}
      role="button"
      aria-label={visible ? 'Hide password' : 'Show password'}
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
    >
      {visible ? (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a20.29 20.29 0 015.06-6.06M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a20.4 20.4 0 01-3.22 4.34M14.12 14.12a3 3 0 11-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </span>
  );
}